import type { FieldErrors } from '../../../lib/validate';
import { storyLoadoutDefaults } from '../../../lib/s4/defaults';
import type { StoryLoadoutDef } from '../../../lib/s4/types';
import { withDefaults } from '../../../lib/teamsDefaults';
import { IssueList, SelectInput, StackListEditor } from '../fields';
import { useItemOptions } from '../refs';

/**
 * An authored story loadout (shared-cs `StoryLoadoutDef`): two weapon slots, worn armor and items (ammo included). Nothing comes from or
 * goes to the run. `base` is the loadout's path for inline issues (`story.loadout`, `cast.2.loadout`).
 */
export function LoadoutEditor({
  label,
  value,
  onChange,
  errors,
  base,
}: {
  label: string;
  value: StoryLoadoutDef | null | undefined;
  onChange: (next: StoryLoadoutDef) => void;
  errors: FieldErrors;
  base: string;
}) {
  const l = withDefaults(storyLoadoutDefaults(), value ?? {});
  const weapons = useItemOptions((c) => c === 'weapon');
  const armor = useItemOptions((c) => c === 'armor');
  const at = (f: string) => errors[base ? `${base}.${f}` : f];
  const slot = (k: 'weapon' | 'weapon2' | 'armor', title: string) => (
    <div>
      <span className="block text-xs font-semibold uppercase tracking-wide">{title}</span>
      <SelectInput
        label={`${label} ${title}`}
        value={l[k]}
        none="none"
        options={k === 'armor' ? armor : weapons}
        invalid={!!at(k)?.some((i) => i.severity === 'error')}
        onChange={(v) => onChange({ ...l, [k]: v ?? '' })}
      />
      <IssueList issues={at(k)} />
    </div>
  );
  return (
    <div className="grid gap-2 sm:col-span-2 sm:grid-cols-3" data-testid={`loadout-${base}`}>
      <p className="text-xs font-semibold sm:col-span-3">{label}</p>
      {slot('weapon', 'Weapon')}
      {slot('weapon2', 'Weapon 2')}
      {slot('armor', 'Armor')}
      <div className="sm:col-span-3">
        <StackListEditor
          label={`${label} items`}
          value={l.items}
          errors={errors}
          base={base ? `${base}.items` : 'items'}
          hint="Ammo included. Whole numbers only."
          onChange={(items) => onChange({ ...l, items })}
        />
      </div>
      <IssueList issues={errors[base]} />
    </div>
  );
}
